// pages/Dashboard.js

import { Box, Grid, Paper, Typography, Button, LinearProgress, Chip, TextField, Autocomplete, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import useCompanyData from "../hooks/useCompanyData";

import React, { useEffect, useState, useMemo } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc, Timestamp, collection, query, where, getCountFromServer, getDocs  } from "firebase/firestore";
import { useAuth } from "../authContext";

import StatTile from "../components/StatTile";
import {
  AssignmentTurnedIn,
  ListAlt,
  Assessment,
  People,
  Business,
  WorkOutline, ChevronRight
} from "@mui/icons-material";

import { serverTimestamp, setDoc } from "firebase/firestore";


import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import { onSnapshot } from "firebase/firestore"; // with your other firestore imports


const formatLoginTime = (ts) => {
  if (!ts) return "";
  // handle Firestore Timestamp or string
  const d = ts instanceof Timestamp ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return "";
  return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
};

const SummaryCard = ({ title, count }) => (
  <Paper sx={{ p: 2, textAlign: "center", minHeight: 110 }}>
    <Typography variant="body1">{title}</Typography>
    <Typography variant="h5" color="primary">{count}</Typography>
  </Paper>
);


// --- date helpers (add here) ---
const toJSDate = (v) => (v?.toDate ? v.toDate() : new Date(v));
const fmtDate  = (d) => (d ? d.toLocaleDateString("en-GB") : "—");
const daysLeft = (d) => Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));

// put near your other helpers (where daysLeft is defined)
const chipColorForStage = (stage, due) => {
  const s = (stage || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "in progress") return "warning";
  return daysLeft(due) < 0 ? "error" : "default";
};

// Build /tasks url including current dashboard filters
const gotoTasks = (extra = {}) => {
  const params = new URLSearchParams();

  // carry dashboard filters
  if (selectedUser?.name) params.set("user", selectedUser.name);

  // add whatever the tile wants to set (e.g., status)
  Object.entries(extra).forEach(([k, v]) => {
    if (v != null && v !== "") params.set(k, v);
  });

  navigate(`/tasks?${params.toString()}`);
};



// ── Compact KPI card just for the task tiles ───────────────────────────────
const KpiCard = ({ title, value, icon, accent = "#1976d2", bg = "#F8FAFF", onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 1.5,
      borderRadius: 3,
      bgcolor: bg,
      border: "1px solid #E7ECF5",
      cursor: "pointer",
      transition: "all .15s ease",
      "&:hover": {
        boxShadow: "0 6px 18px rgba(16,24,40,.08)",
        transform: "translateY(-2px)",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          bgcolor: "#FFFFFF",
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,.06)",
          color: accent,
          flex: "0 0 auto",
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1, mt: 0.5, color: accent }}>
          {value}
        </Typography>
      </Box>

      <ChevronRight sx={{ color: "text.disabled" }} />
    </Box>
  </Paper>
);



// --- end helpers ---

// Compact metric tile for summary counts (Entities / Projects / Users)
const MetricTile = ({ title, value, icon, accent = "#111827", tint = "#F8FAFF", onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 1.5,
      borderRadius: 3,
      bgcolor: "#FFFFFF",
      border: "1px solid #E7ECF5",
      cursor: "pointer",
      transition: "all .15s ease",
      "&:hover": { boxShadow: "0 6px 18px rgba(16,24,40,.08)", transform: "translateY(-2px)" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          bgcolor: tint,
          color: accent,
          flex: "0 0 auto",
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,.05)",
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" color="text.secondary" noWrap>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1, mt: 0.5, color: accent }}>
          {value}
        </Typography>
      </Box>

      <ChevronRight sx={{ color: "text.disabled" }} />
    </Box>
  </Paper>
);


  export default function Dashboard() {
  const data = useCompanyData() || {};
  const { companyId, currentUser, companyData, claims } = useAuth(); // companyData/claims exist in your logs
  const navigate = useNavigate();

  const db = getFirestore();

  const [prevLogin, setPrevLogin] = useState("");
  const [usersCount, setUsersCount] = useState(0);

  const [roleFromDb, setRoleFromDb] = useState("");

// Global user filter (Admin/Manager only)
const [selectedUser, setSelectedUser] = useState(null); // {id, name} or null
const [companyUsers, setCompanyUsers] = useState([]);   // [{id, name}]

const [unread, setUnread] = useState(0);

const [tlTasks, setTlTasks] = useState([]);

// Build /tasks url including current dashboard filters
const gotoTasks = (extra = {}) => {
  const params = new URLSearchParams();

  // carry dashboard "Filter by user"
  if (selectedUser?.name) params.set("user", selectedUser.name);

  // tile-specific additions (e.g., status)
  if (extra.status) params.set("status", extra.status);

  navigate(`/tasks?${params.toString()}`);
};


// name(idNorm) -> { isActive, category, ... }
const userMetaByName = useMemo(() => {
  const m = new Map();
  companyUsers.forEach(u => m.set(
    (u.name || "").toString().trim().toLowerCase().replace(/[^a-z0-9]/g, ""),
    u
  ));
  return m;
}, [companyUsers]);


// New dashboard-wide user filters
const CATEGORY_OPTIONS = [
  "Leadership (owners, managing partners)",
  "Engagement Heads (Experienced Professionals)",
  "Qualified Professionals (CA, CS, ACCA or Other Post Graduate qualifications)",
  "Semi-Qualified Staff (pursuing professional course)",
  "Trainee’s (Interns etc)",
  "Support Staff (other employees)",
  "HR (internal administrative staff)",
];
const [filterCategories, setFilterCategories] = useState([]); // [] of strings
const [filterStatus, setFilterStatus] = useState("");         // "", "Active", "Inactive"

  // default: today → +7 days
const todayISO = new Date().toISOString().slice(0, 10);
const next7ISO = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
const [dueFrom, setDueFrom] = useState(todayISO);
const [dueTo,   setDueTo]   = useState(next7ISO);




  useEffect(() => {
  const u = getAuth().currentUser;
  if (!u) return;

  const db = getFirestore();
  const q = query(
    collection(db, "users", u.uid, "notifications"),
    where("read", "==", false)
  );

  const unsub = onSnapshot(q, (snap) => setUnread(snap.size));
  return () => unsub();
}, []);




  useEffect(() => {
  const auth = getAuth();
  const db = getFirestore();
  const user = auth.currentUser;
  if (!user) return;

  // Only fetch if we don't already have a role
  const hasRole =
    (currentUser?.role && currentUser.role.trim()) ||
    (companyData?.role && companyData.role.trim()) ||
    (claims?.role && claims.role.trim());

  if (hasRole) return;

  (async () => {
    try {
      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);
      setRoleFromDb((snap.exists() && (snap.data()?.role || "")) || "");
    } catch (e) {
      console.error("Failed to fetch role from Firestore:", e);
      setRoleFromDb("");
    }
  })();
}, [currentUser, companyData, claims]);




  useEffect(() => {
    const auth = getAuth();
    
  const db = getFirestore();
  const unsub = onAuthStateChanged(auth, async (user) => {
    if (!user) { setPrevLogin(""); return; }
    // Read previousLoginAt from Firestore
    const ref = doc(db, "users", user.uid);
    const snap = await getDoc(ref);
    setPrevLogin(snap.exists() ? snap.data()?.previousLoginAt || "" : "");
  });

    return () => unsub();
  }, []);


  // ✅ NEW EFFECT — fetch users count for this company
  useEffect(() => {
    const db = getFirestore();
    if (!companyId) return;

  
    (async () => {
      try {
        const q = query(collection(db, "users"), where("companyId", "==", companyId));
        const agg = await getCountFromServer(q);
        setUsersCount(agg.data().count || 0);
      } catch (e) {
        console.error("Failed to load users count:", e);
        setUsersCount(0);
      }
    })();
  }, [companyId]);


useEffect(() => {
  const u = getAuth().currentUser;
  if (!u) return;
  const today = new Date();
  const id = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
  setDoc(doc(db, "users", u.uid, "loginDays", id), { loginAt: serverTimestamp() }, { merge: true })
    .catch((e) => console.error("loginDays write failed:", e));
}, [db]);

  
  const {
  entities = [],
  projects = [],
  tasks = [],
  counts = { totalEntities: 0, totalProjects: 0, totalTasks: 0, users: 0, userName: "" },
} = data;



// ---------- helpers ----------
// ---------- helpers ----------


// ---------- helpers ----------
const norm = (s) => (s || "").trim().toLowerCase();

 // normalize for id/name comparisons
 const idNorm = (s) => (s || "").toString().trim().toLowerCase().replace(/[^a-z0-9]/g, "");
 const viewerUid  = currentUser?.uid || getAuth().currentUser?.uid || "";

 const viewerName = idNorm(
   currentUser?.name ||
   currentUser?.displayName ||
   data?.counts?.userName ||
   ""
);



// Pull role from any available source, then normalize
const rawRole =
  currentUser?.role ??
  companyData?.role ??
  claims?.role ??
  roleFromDb ??                    // <-- Firestore fallback
  "";

const role = norm(rawRole);

// Admin/Manager should see company-wide; Member sees scoped
const isAdminOrManager = role.includes("admin") || role.includes("manager");
const isMember = !isAdminOrManager; // default to member if not admin/manager




// Load company users list for Admin/Manager
useEffect(() => {
  if (!companyId || !isAdminOrManager) return;
  const db = getFirestore();

  (async () => {
    try {
      const q = query(collection(db, "users"), where("companyId", "==", companyId));
      const snap = await getDocs(q);
      const arr = snap.docs.map(d => {
        const u = d.data() || {};
        const name = u.name || u.displayName || u.email || "Unnamed";
         return {
          id: d.id,
          name,
          isActive: (u.isActive ?? true) === true,
          category: u.category || "",
        };
      });
      arr.sort((a, b) => a.name.localeCompare(b.name));
      setCompanyUsers(arr);
    } catch (e) {
      console.error("Failed to load users list:", e);
      setCompanyUsers([]);
    }
  })();
}, [companyId, isAdminOrManager]);




//const viewerName =
  //norm(currentUser?.name) ||
  //norm(currentUser?.displayName) ||
  //norm(data?.counts?.userName);

// (optional) quick debug
console.log({
  rawRole,
  role,
  isMember,
  viewerName,
  from: {
    currentUserRole: currentUser?.role,
    companyDataRole: companyData?.role,
    claimsRole: claims?.role,
    roleFromDb,
  },
});

const normStatus = (s) => {
  const n = norm(s);
  if (n === "completed") return "completed";
  if (n === "in progress" || n === "progress") return "in-progress";
  if (["pending", "to do", "todo", "not started"].includes(n)) return "pending";
  return n;
};



// ---------- task scope ----------
// Base task scope by role (Members see only their own; Admin/Manager see all)
 const tasksBase = isMember
   ? tasks.filter(t => idNorm(t.assignedTo) === viewerName)
   : tasks;

const isFilteredView = isAdminOrManager && Boolean(selectedUser);

// Apply global user filter (Admin/Manager only)
// Apply global user / category / status filters (Admin/Manager only)
const tasksForView = useMemo(() => {
  let out = tasksBase;

  // 1) “Filter by user”
  if (isAdminOrManager && selectedUser) {
    const sel = idNorm(selectedUser.name);
    out = out.filter(t => idNorm(t.assignedTo) === sel);
  }

  // 2) Category + Status (Active/Inactive) filters
  if (isAdminOrManager && (filterCategories.length || filterStatus)) {
    out = out.filter(t => {
      const meta = userMetaByName.get(idNorm(t.assignedTo));
      if (!meta) return false; // unknown assignee, hide
      const okCat = filterCategories.length
        ? filterCategories.includes(meta.category || "")
        : true;
      const okStatus = filterStatus
        ? (filterStatus === "Active" ? meta.isActive : !meta.isActive)
        : true;
      return okCat && okStatus;
    });
  }
  return out;
}, [tasksBase, selectedUser, isAdminOrManager, filterCategories, filterStatus, userMetaByName]);



 // projects I manage (by id OR by managerName)
 const managedProjectNames = useMemo(() => {
   const s = new Set(
     (projects || [])
       .filter(p => p?.managerId === viewerUid || idNorm(p?.managerName) === viewerName)
       .map(p => (p?.name || "").trim())
       .filter(Boolean)
   );
   return s;
 }, [projects, viewerUid, viewerName]);

// All tasks grouped by project (company-wide)
 const tasksAllForScope = useMemo(() => {
  const uniq = new Map();
  [...(tasks || []), ...(tlTasks || [])].forEach(t => {
    const key = t.id || `${t.project}|${t.taskName || t.name}|${t.dueDate}`;
    uniq.set(key, t);
  });
  return Array.from(uniq.values());
}, [tasks, tlTasks]);

const byProjectAll = useMemo(() => {
  const m = new Map();
  for (const t of tasksAllForScope) {
    const key = norm(t?.project);
    if (!key) continue;
    if (!m.has(key)) m.set(key, []);
    m.get(key).push(t);
  }
  return m;
}, [tasksAllForScope]);



 // projects I am Team Lead for (by id OR by teamLeadName)
 // projects I am Team Lead for (by id OR by name)
const tlProjectNames = useMemo(() => {
  const s = new Set(
    (projects || [])
      .filter(p =>
        (p?.teamLeadId === viewerUid) ||
        (p?.teamLeaderId === viewerUid) ||
        (p?.teamLeadUid === viewerUid) ||
        idNorm(
          p?.teamLeadName ||
          p?.teamLeaderName ||
          p?.teamLeader ||
          p?.teamLead
        ) === viewerName
      )
      .map(p => (p?.name || "").trim())
      .filter(Boolean)
  );
  return s;
}, [projects, viewerUid, viewerName]);





useEffect(() => {
  // Only for member-type users (including Team Leaders)
  if (!companyId || !isMember) { setTlTasks([]); return; }
  if (!tlProjectNames?.size) { setTlTasks([]); return; }

  (async () => {
    try {
      const db = getFirestore();
      const names = Array.from(tlProjectNames).map(n => (n || "").trim()).filter(Boolean);

      // Firestore "in" supports max 10 values, so batch
      const batches = [];
      for (let i = 0; i < names.length; i += 10) batches.push(names.slice(i, i + 10));

      const out = [];
      for (const batch of batches) {
        const q = query(
          collection(db, "tasks"),
          where("companyId", "==", companyId),
          where("project", "in", batch)
        );
        const snap = await getDocs(q);
        snap.docs.forEach(d => out.push({ id: d.id, ...d.data() }));
      }

      // de-dup
      const uniq = new Map();
      out.forEach(t => uniq.set(t.id || `${t.project}|${t.taskName || t.name}|${t.dueDate}`, t));
      setTlTasks(Array.from(uniq.values()));
    } catch (e) {
      console.error("Failed to load TL tasks for dashboard tiles:", e);
      setTlTasks([]);
    }
  })();
}, [companyId, isMember, tlProjectNames]);


  

// Role-aware scope for STAT TILES (Admin = all; Manager = only managed projects; Member = own tasks + ALL tasks from TL projects)
// Uses helpers & sets declared above (norm, managedProjectNames, tlProjectNames, byProjectAll, viewerName, role, selectedUser)
const tasksForTiles = useMemo(() => {
  // Admin/Manager viewing a specific user → only that user's tasks
  if (isAdminOrManager && selectedUser) {
    const sel = idNorm(selectedUser.name);
    return (tasks || []).filter(t => idNorm(t.assignedTo) === sel);
  }

  // Admin → all company tasks
  if (role.includes("admin")) return tasks || [];

  // Manager → ONLY tasks under projects I manage (all tasks under those projects)
  if (role.includes("manager")) {
    const allow = managedProjectNames; // Set of trimmed names
    return (tasks || []).filter(t => allow.has((t.project || "").trim()));
  }

  // Member →
  // - Always my own assigned tasks
  // - PLUS: for any project I'm TL for, include ALL tasks under that project
  const mine = (tasks || []).filter(t => idNorm(t.assignedTo) === viewerName);
  if (!tlProjectNames?.size) return mine;

  const extras = [];
  tlProjectNames.forEach((name) => {
    const arr = byProjectAll.get(norm(name)) || [];
    extras.push(...arr);
  });

  // De-dup by id (or stable fallback)
  const uniq = new Map();
  [...mine, ...extras].forEach(t => {
    const key = t.id || `${t.project}|${t.taskName || t.name}|${t.dueDate}`;
    uniq.set(key, t);
  });
  return Array.from(uniq.values());
}, [isAdminOrManager, selectedUser, role, tasks, managedProjectNames, tlProjectNames, byProjectAll, viewerName]);

 // Unique users from the scope used by the stat tiles
 const usersFromTiles = useMemo(() => {
   return new Set(
     (tasksForTiles || [])
       .map(t => (t.assignedTo || "").trim())
       .filter(Boolean)
   );
 }, [tasksForTiles]);

// ---------- second row (by status) ----------
const totalTasks      = tasksForTiles.length;
const completedTasks  = tasksForTiles.filter(t => norm(t.status) === "completed").length;
const inProgressTasks = tasksForTiles.filter(t => norm(t.status) === "in progress").length;
const pendingTasks    = tasksForTiles.filter(t =>
  ["pending", "not started", "to do", "todo"].includes(norm(t.status))
).length;



// Group visible tasks by normalized project name
// 🔎 NEW: group the *filtered* scope (tasksForView) by project as well.
// We’ll use this for Upcoming cards so Category/Status/User filters apply.
const byProjectFiltered = useMemo(() => {
  const m = new Map();
  for (const t of tasksForView) {
    const key = norm(t.project);
    if (!key) continue;
    if (!m.has(key)) m.set(key, []);
    m.get(key).push(t);
  }
  return m;
}, [tasksForView]);

// Member’s own project names (from current view scope)
const memberProjects = useMemo(() => {
  return new Set(
    (tasksForView || [])
      .map(t => (t.project || "").trim())
      .filter(Boolean)
  );
}, [tasksForView]);

 
 const isManagerOf = (p) =>
   p?.managerId === viewerUid || idNorm(p?.managerName) === viewerName;
 const isTL = (p) =>
   p?.teamLeadId === viewerUid || idNorm(p?.teamLeadName || p?.teamLeaderName) === viewerName;


// ---------- first row (entities / projects / users) ----------
const projectToEntity = projects.reduce((acc, p) => {
  acc[norm(p.name)] = (p.entity || "").trim();
  return acc;
}, {});

// Projects/entities from the TILE scope
const projectsFromTiles = new Set(
  tasksForTiles.map(t => (t.project || "").trim()).filter(Boolean)
);
const entitiesFromTiles = new Set(
  tasksForTiles
    .map(t => (t.entity || projectToEntity[norm(t.project)] || "").trim())
    .filter(Boolean)
);
// If Member: include TL projects/entities even if they currently have no "my" tasks
const tlEntities = new Set(
  (projects || [])
    .filter(p => isTL(p))
    .map(p => (p.entity || "").trim())
    .filter(Boolean)
);
const memberProjectScope = new Set([...projectsFromTiles, ...tlProjectNames]);
const memberEntityScope  = new Set([...entitiesFromTiles, ...tlEntities]);


// ✅ If Member OR Admin/Manager with a selected user, use the filtered sets.
// Otherwise (no user selected) show company-wide counts.
const totalEntities = (() => {
  // Company-wide for Admin with no user filter
  if (role.includes("admin") && !isFilteredView) return counts.totalEntities ?? entities.length;
  // Manager with no user filter → entities from projects they manage
  if (role.includes("manager") && !isFilteredView) {
    const s = new Set(
      (projects || [])
        .filter(isManagerOf)
        .map(p => (p.entity || "").trim())
        .filter(Boolean)
    );
    return s.size;
  }
  // Member OR Admin/Manager with a selected user → from scoped tasks
  return (isMember ? memberEntityScope : entitiesFromTiles).size;
})();

const totalProjects = (() => {
  if (role.includes("admin") && !isFilteredView) return counts.totalProjects ?? projects.length;
  if (role.includes("manager") && !isFilteredView) return (projects || []).filter(isManagerOf).length;
  // Member OR Admin/Manager with a selected user
  return (isMember ? memberProjectScope : projectsFromTiles).size;
})();



 const totalUsers = (() => {
   // Member, or Admin/Manager viewing a specific user → 1
   if (isMember || isFilteredView) return 1;
   // Admin (no user filter) → company total
   if (role.includes("admin")) return (counts.users ?? usersCount);
   // Manager (no user filter) → users within projects I manage
   return usersFromTiles.size;
 })();

// Unique users from the scope used by the stat tiles (already added earlier)
// const usersFromTiles = useMemo(() => new Set(...), [tasksForTiles]);

// Role-aware options for the "Filter by user" dropdown
const usersForFilter = useMemo(() => {
  // Admin (no restriction): all company users
  if (role.includes("admin")) {
    return [...(companyUsers || [])].sort((a, b) =>
      (a?.name || "").localeCompare(b?.name || "")
    );
  }

  // Manager: only users who appear in tasks within projects I manage
  const allow = new Set(
    [...(usersFromTiles || [])].map((n) => (n || "").trim().toLowerCase())
  );
  const matched = (companyUsers || []).filter((u) =>
    allow.has((u?.name || "").trim().toLowerCase())
  );

  // Also include any names that exist in tasks but aren't in companyUsers (fallback stub)
  const missing = [...(usersFromTiles || [])]
    .filter(
      (n) =>
        !matched.some(
          (u) => (u?.name || "").trim().toLowerCase() === (n || "").trim().toLowerCase()
        )
    )
    .map((n) => ({ uid: `name:${n}`, name: n }));

  return [...matched, ...missing].sort((a, b) =>
    (a?.name || "").localeCompare(b?.name || "")
  );
}, [role, companyUsers, usersFromTiles]);

// If the selected user falls outside the scoped list, clear it
useEffect(() => {
  if (!selectedUser) return;
  const present = (usersForFilter || []).some((u) =>
    u?.uid && selectedUser?.uid
      ? u.uid === selectedUser.uid
      : (u?.name || "").trim().toLowerCase() === (selectedUser?.name || "")
          .trim()
          .toLowerCase()
  );
  if (!present) setSelectedUser(null);
}, [usersForFilter, selectedUser, setSelectedUser]);


// Constrain users by Status + Category
const usersForFilterScoped = useMemo(() => {
  let arr = usersForFilter;

  if (filterStatus) {
    const wantActive = filterStatus === "Active";
    arr = arr.filter((u) => (u.isActive ?? true) === wantActive);
  }
  if (filterCategories.length) {
    const set = new Set(filterCategories);
    arr = arr.filter((u) => set.has(u.category || ""));
  }
  return [...arr].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
}, [usersForFilter, filterStatus, filterCategories]);

// Category list constrained by currently valid users
const categoryOptionsScoped = useMemo(() => {
  const set = new Set(
    usersForFilterScoped.map((u) => u.category || "").filter(Boolean)
  );
  return CATEGORY_OPTIONS.filter((c) => set.has(c));
}, [usersForFilterScoped]);

// Keep selected user valid if the options shrink
useEffect(() => {
  if (!selectedUser) return;

  const present = usersForFilterScoped.some((u) => {
    if (u?.id && selectedUser?.id) return u.id === selectedUser.id;
    if (u?.uid && selectedUser?.uid) return u.uid === selectedUser.uid;
    return (u?.name || "").trim().toLowerCase() ===
           (selectedUser?.name || "").trim().toLowerCase();
  });

  if (!present) setSelectedUser(null);
}, [usersForFilterScoped, selectedUser]);

// Keep categories valid if options shrink
useEffect(() => {
  if (!filterCategories.length) return;
  const allowed = new Set(categoryOptionsScoped);
  const next = filterCategories.filter((c) => allowed.has(c));
  if (next.length !== filterCategories.length) setFilterCategories(next);
}, [categoryOptionsScoped]);


// ---------- TEMP DEBUG (remove after verifying) ----------
console.log({
  rawRole, role, isMember,
  viewerName,
  firstRow: { totalEntities, totalProjects, totalUsers },
  tasksForView: tasksForView.length
});

  
// projects visible to the viewer (Admin/Manager: all; Member: only their projects)
// ✅ If Member OR filtering by a user, show only that user’s projects
const projectNameSetForScope = memberProjects; // from tasksForView

 const visibleProjects = useMemo(() => {
   if (!projects?.length) return [];
   // Admin: see everything
   if (!isMember && !isFilteredView && role.includes("admin")) return projects;
   // Manager (no user selected): only projects I manage
   if (!isMember && !isFilteredView) {
     return projects.filter(isManagerOf);
   }
   // Admin/Manager when a specific user is selected → show only that user's projects
   if (!isMember && isFilteredView) {
     return projects.filter(p => byProjectFiltered.has(norm(p?.name)));
   }
   // Member: projects I have tasks on + any project I am TL for
   return projects.filter(p =>
     projectNameSetForScope.has((p?.name || "").trim()) || isTL(p)
   );
 }, [projects, isMember, isFilteredView, role, byProjectFiltered, projectNameSetForScope, isManagerOf, isTL]);


// filter by date range (inclusive)
const fromDate = new Date(dueFrom + "T00:00:00");
const toDate   = new Date(dueTo   + "T23:59:59");



const upcomingProjects = visibleProjects
  .map(p => {
    const due = p.dueDate ? toJSDate(p.dueDate) : null;
    if (!due || isNaN(due)) return null;

    // pull all tasks under this project (only the viewer’s scope for Members)
    //const arr = byProject.get(norm(p.name)) || [];

   // ✅ Use the filtered map for Admin/Manager so Category/Status/User filters apply.
   const key = norm(p.name);
   let arr = [];
   if (isAdminOrManager) {
  arr = byProjectFiltered.get(key) || [];
} else {
  // Member: TL → all tasks under project; else → only my tasks (filtered map)
  arr = isTL(p) ? (byProjectAll.get(key) || []) : (byProjectFiltered.get(key) || []);
}


    const total = arr.length;

    let completed = 0, inProg = 0, pending = 0;
    for (const t of arr) {
      const s = normStatus(t.status);
      if (s === "completed") completed++;
      else if (s === "in-progress") inProg++;
      else if (s === "pending") pending++;
    }

    const completion = total ? Math.round((completed / total) * 100) : 0;
    const stage =
      total === 0 ? (p.status || p.priority || "—")
      : completed === total ? "Completed"
      : inProg > 0 ? "In Progress"
      : "Pending";

    return {
      entity: p.entity || "—",
      name: p.name,
      due,
      // NEW:
      completion,
      stage
    };
  })
  .filter(Boolean)
  .filter(({ due }) => due >= fromDate && due <= toDate)
  .sort((a, b) => a.due - b.due)
  .slice(0, 10);


// Build role-aware task scope for the "Upcoming Task Deadlines" card
// ✅ Admin/Manager → always use the filtered scope (respects User/Category/Status)
  // Build role-aware task scope for the "Upcoming Task Deadlines" card
const taskScopeForUpcoming = useMemo(() => {
  // Admin/Manager → use the filtered scope (User/Category/Status)
  if (isAdminOrManager) return tasksForView;

  // Member:
  // - own tasks
  // - plus ALL tasks under projects where I'm TL
  let mine = tasksForView || [];
  if (!tlProjectNames?.size) return mine;

  const extras = [];
  tlProjectNames.forEach((name) => {
    const arr = byProjectAll.get(norm(name)) || [];
    extras.push(...arr);
  });

  // de-duplicate by task id (fallback key if id missing)
  const uniq = new Map();
  [...mine, ...extras].forEach((t) => {
    const key = t.id || `${t.project}|${t.taskName || t.name}|${t.dueDate}`;
    uniq.set(key, t);
  });
  return Array.from(uniq.values());
}, [isAdminOrManager, tasksForView, tlProjectNames, byProjectAll]);



// upcoming tasks for the same date range (respecting role scoping via tasksForView)
// upcoming tasks for the same date range (role-aware scope)
const upcomingTasks = (taskScopeForUpcoming || [])
  .map(t => {
    const d = t.dueDate ? toJSDate(t.dueDate) : null;
    if (!d || isNaN(d)) return null;
    return {
      entity: t.entity || projectToEntity[norm(t.project)] || "—",
      project: (t.project || "").trim(),
      task: (t.taskName || t.title || t.name || "Untitled").trim(),
      assignee: (t.assignedTo || "").trim(),
      status: (t.status || "").trim(),
      due: d,
    };
  })
  .filter(Boolean)
  .filter(({ due }) => {
    const from = new Date(dueFrom + "T00:00:00");
    const to   = new Date(dueTo   + "T23:59:59");
    return due >= from && due <= to;
  })
  .sort((a, b) => a.due - b.due)
  .slice(0, 15);


  return (
     <Box sx={{ px: 2, py: 1 }}>
     {/* Header */}
    {/* Row 1: title + welcome */}
<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
  <Typography variant="h4">Dashboard</Typography>

  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
    {/* Unread status chip – click opens notifications page */}
    <Chip
      size="small"
      variant="outlined"
      label={`${unread} unread`}
      onClick={() => navigate("/notifications")}
      sx={{ cursor: "pointer" }}
    />

    {/* Bell with badge */}
    <IconButton
      aria-label="Notifications"
      onClick={() => navigate("/notifications")}
      size="large"
    >
      <Badge color="error" badgeContent={unread} overlap="circular">
        <NotificationsNoneIcon />
      </Badge>
    </IconButton>

    <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 0.5 }}>
      Welcome, {counts.userName}
      {prevLogin ? ` · Previous login: ${formatLoginTime(prevLogin)}` : ""}
    </Typography>
  </Box>
</Box>


{/* Filters row (User + Category + Status) */}
{isAdminOrManager && (
  <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 3 }}>
    
{/* Filter by user (existing) */}
    <Autocomplete
      size="small"
      sx={{ width: 320 }}
      options={usersForFilterScoped}
      value={selectedUser}
      onChange={(_, v) => setSelectedUser(v)}
      getOptionLabel={(o) => o?.name ?? ""}
      
isOptionEqualToValue={(opt, val) => {
  if (!opt || !val) return false;
  if (opt.id && val.id) return opt.id === val.id;     // Firestore users
  if (opt.uid && val.uid) return opt.uid === val.uid; // stub fallback
  return (opt.name || "").trim().toLowerCase() === (val.name || "").trim().toLowerCase();
}}

      renderInput={(params) => (
        <TextField
          {...params}
          label="Filter by user"
          placeholder={role.includes("admin") ? "All users" : "Users in your projects"}
        />
      )}
    />

    {/* Status - toggle */}
    <ToggleButtonGroup
      exclusive
      size="small"
      value={filterStatus || "All"}
      onChange={(_, v) => setFilterStatus(v === "All" ? "" : v)}
    >
      <ToggleButton value="All">All</ToggleButton>
      <ToggleButton value="Active">Active</ToggleButton>
      <ToggleButton value="Inactive">Inactive</ToggleButton>
    </ToggleButtonGroup>

    
{/* Category – works like the user Autocomplete */}
<Autocomplete
  multiple
  size="small"
  sx={{ width: 320 }}
  options={categoryOptionsScoped}                 // string[]
  value={filterCategories}                   // string[]
  onChange={(_, v) => setFilterCategories(v)}
  getOptionLabel={(o) => (typeof o === "string" ? o : o?.label ?? "")}
  isOptionEqualToValue={(opt, val) =>
    (typeof opt === "string" ? opt : opt?.value) ===
    (typeof val === "string" ? val : val?.value)
  }
  renderInput={(params) => (
    <TextField
      {...params}
      label="Category"
      placeholder="Search categories…"
    />
  )}
/>



    {selectedUser && (
      <Button size="small" onClick={() => setSelectedUser(null)}>Clear</Button>
    )}
  </Box>
)}



      <Grid container spacing={2} sx={{ mb: 4 }}>
  <Grid item xs={12} md={4}>
    <StatTile
      title="Total Entities"
      value={totalEntities}
      icon={<Business />}
      tint="#FFFFFF"
      iconBg="#FFF3CD"      // soft yellow
      onClick={() => navigate("/entities")}
    />
  </Grid>

  <Grid item xs={12} md={4}>
    <StatTile
      title="Total Projects"
      value={totalProjects}
      icon={<WorkOutline />}
      tint="#FFFFFF"
      iconBg="#E3F2FD"      // soft blue
      onClick={() => navigate("/projects")}
    />
  </Grid>

  
  <Grid item xs={12} md={4}>
    <StatTile
      title="Users"
      value={totalUsers}
      icon={<People />}
      tint="#FFFFFF"
      iconBg="#E8EAF6"      // soft indigo
      onClick={() => navigate("/users")}
    />
  </Grid>

</Grid>

<Grid container spacing={2} sx={{ mb: 2 }}>
  <Grid item xs={12} md={3}>
    <KpiCard
      title="Total Tasks"
      value={totalTasks}
      icon={<ListAlt />}
      accent="#0EA5E9"   // sky
      bg="#F0F9FF"
      onClick={() => gotoTasks()}
    />
  </Grid>

  <Grid item xs={12} md={3}>
    <KpiCard
      title="Completed Tasks"
      value={completedTasks}
      icon={<AssignmentTurnedIn />}
      accent="#16A34A"   // green
      bg="#ECFDF5"
      onClick={() => gotoTasks({ status: "Completed" })}
    />
  </Grid>

  <Grid item xs={12} md={3}>
    <KpiCard
      title="In Progress Tasks"
      value={inProgressTasks}
      icon={<Assessment />}
      accent="#F59E0B"   // amber
      bg="#FFFBEB"
      onClick={() => gotoTasks({ status: "In Progress" })}
    />
  </Grid>

  <Grid item xs={12} md={3}>
    <KpiCard
      title="Pending Tasks"
      value={pendingTasks}
      icon={<ListAlt />}
      accent="#f16363ff"   // indigo
      bg="#f6e3e3ff"
      onClick={() => gotoTasks({ status: "Pending" })}
    />
  </Grid>
</Grid>


      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          
<Paper
  elevation={0}
  sx={{
    p: 3,
    borderRadius: 3,
    bgcolor: '#F5F8FF',          // solid light blue
    border: '1px solid #E3EDFF', // subtle outline
    boxShadow: '0 2px 10px rgba(16,24,40,.06)',
    transition: 'box-shadow .15s ease, transform .15s ease',
    '&:hover': { boxShadow: '0 6px 18px rgba(16,24,40,.10)', transform: 'translateY(-1px)' },
    display: "flex", alignItems: "center", justifyContent: "space-between"
  }}
>

            <Box>
              <Typography variant="h6">Project Information</Typography>
              <Typography variant="body2" color="text.secondary">
                Progress by project, deadlines and stage.
              </Typography>
            </Box>
            <Button component={Link} to="/projects-info" variant="contained">Open</Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          
<Paper
  elevation={0}
  sx={{
    p: 3,
    borderRadius: 3,
    bgcolor: '#F5F8FF',          // solid light blue
    border: '1px solid #E3EDFF', // subtle outline
    boxShadow: '0 2px 10px rgba(16,24,40,.06)',
    transition: 'box-shadow .15s ease, transform .15s ease',
    '&:hover': { boxShadow: '0 6px 18px rgba(16,24,40,.10)', transform: 'translateY(-1px)' },
    display: "flex", alignItems: "center", justifyContent: "space-between"
  }}
>

            <Box>
              <Typography variant="h6">User & Task Summary</Typography>
              <Typography variant="body2" color="text.secondary">
                Tasks grouped by entity · user · project.
              </Typography>
            </Box>
            <Button component={Link} to="/user-task-summary" variant="contained">Open</Button>
          </Paper>
        </Grid>

        {/* Login Activity card */}
{/* Login Activity card */}
<Grid item xs={12} md={4}>
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      bgcolor: '#F5F8FF',
      border: '1px solid #E3EDFF',
      boxShadow: '0 2px 10px rgba(16,24,40,.06)',
      transition: 'box-shadow .15s ease, transform .15s ease',
      '&:hover': { boxShadow: '0 6px 18px rgba(16,24,40,.10)', transform: 'translateY(-1px)' },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Box>
      <Typography variant="h6">Login Snapshot</Typography>
      <Typography variant="body2" color="text.secondary">
        Month-wise days a user logged in.
      </Typography>
    </Box>
    <Button component={Link} to="/login-activity" variant="contained">
      Open
    </Button>
  </Paper>

</Grid>
</Grid>


<Grid container spacing={2} sx={{ mt: 2 }}>
  <Grid item xs={12} md={6}>
    <Paper
      elevation={0}
      sx={{
        p: 3, borderRadius: 3, bgcolor: '#e8e9fdff',
        border: '1px solid #E3EDFF',
        boxShadow: '0 2px 10px rgba(16,24,40,.06)',
        transition: 'box-shadow .15s ease, transform .15s ease',
        '&:hover': { boxShadow: '0 6px 18px rgba(16,24,40,.10)', transform: 'translateY(-1px)' },
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography variant="h6">Timesheet</Typography>
        <Typography variant="body2" color="text.secondary">
          Weekly hours by Project.
        </Typography>
      </Box>
      <Button variant="contained" onClick={() => navigate("/timesheet")}>
        OPEN
      </Button>
    </Paper>
  </Grid>

  <Grid item xs={12} md={6}>
    <Paper
      elevation={0}
      sx={{
        p: 3, borderRadius: 3, bgcolor: '#e8e9fdff',
        border: '1px solid #E3EDFF',
        boxShadow: '0 2px 10px rgba(16,24,40,.06)',
        transition: 'box-shadow .15s ease, transform .15s ease',
        '&:hover': { boxShadow: '0 6px 18px rgba(16,24,40,.10)', transform: 'translateY(-1px)' },
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography variant="h6">Timesheet Insights</Typography>
        <Typography variant="body2" color="text.secondary">
          Time spent by project & user (monthly).
        </Typography>
      </Box>
      <Button component={Link} to="/timesheet-insights" variant="contained">
        Open
      </Button>
    </Paper>
  </Grid>
</Grid>


  
  {/* Upcoming deadlines snapshot */}
<Grid container spacing={2} sx={{ mt: 2 }}>
  <Grid item xs={12}>
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Upcoming Project Deadlines (upto 10 Projects) </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>From</Typography>
            <input
              type="date"
              value={dueFrom}
              onChange={(e) => setDueFrom(e.target.value)}
              style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>To</Typography>
            <input
              type="date"
              value={dueTo}
              onChange={(e) => setDueTo(e.target.value)}
              style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
            />
          </Box>
          <Button size="small" variant="contained" onClick={() => navigate("/projects-info")}>
            View All
          </Button>
        </Box>
      </Box>

      {upcomingProjects.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No projects due in the selected range.
        </Typography>
      ) : (
       <Box
  component="table"
  sx={{
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
    fontSize: "0.875rem",
    color: "rgba(0, 0, 0, 0.87)",
  }}
>
  <thead>
    <tr>
      <th style={{ textAlign: "left", padding: "8px", fontWeight: 600 }}>Entity</th>
      <th style={{ textAlign: "left", padding: "8px", fontWeight: 600 }}>Project</th>
      <th style={{ textAlign: "left", padding: "8px", fontWeight: 600 }}>Due Date</th>
      <th style={{ textAlign: "left", padding: "8px", fontWeight: 600 }}>Time Left</th>
      <th style={{ textAlign: "left", padding: "8px", fontWeight: 600 }}>Task Completion</th>
      <th style={{ textAlign: "left", padding: "8px", fontWeight: 600 }}>Task Stage</th>
    </tr>
  </thead>

  <tbody>
    {upcomingProjects.map((p, i) => {
      const left = daysLeft(p.due);
      const badge =
        left < 0 ? { text: "Overdue", bg: "#ffebee", color: "#d32f2f" } :
        left === 0 ? { text: "Today", bg: "#fff8e1", color: "#f57c00" } :
        { text: `${left} day${left === 1 ? "" : "s"}`, bg: "#e3f2fd", color: "#1976d2" };

      return (
        <tr key={i} style={{ borderTop: "1px solid #eee" }}>
          <td style={{ padding: "10px 4px" }}>{p.entity}</td>
          <td style={{ padding: "10px 4px" }}>{p.name}</td>
          <td style={{ padding: "10px 4px" }}>{fmtDate(p.due)}</td>
          <td style={{ padding: "10px 4px" }}>
            <span style={{
              background: badge.bg,
              color: badge.color,
              padding: "2px 8px",
              borderRadius: 12,
              fontSize: 12
            }}>
              {badge.text}
            </span>
          </td>
<td style={{ padding: "10px 4px", minWidth: 200 }}>
  <Box display="flex" alignItems="center" gap={1}>
    <Box flex={1}><LinearProgress variant="determinate" value={p.completion} /></Box>
    <Typography variant="body2" sx={{ minWidth: 36, textAlign: "right" }}>{p.completion}%</Typography>
  </Box>
</td>
<td style={{ padding: "10px 4px" }}>
  <Chip size="small" label={p.stage || "Pending"} color={chipColorForStage(p.stage, p.due)} />

</td>

        </tr>
      );
    })}
  </tbody>
</Box>
      )}
    </Paper>
  </Grid>
</Grid>


{/* Upcoming task deadlines snapshot */}
<Grid container spacing={2} sx={{ mt: 2 }}>
  <Grid item xs={12}>
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Upcoming Task Deadlines (upto 15 Tasks)</Typography>

        {/* Reuse the same date range controls */}
        <Box display="flex" gap={2} alignItems="center">
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>From</Typography>
            <input
              type="date"
              value={dueFrom}
              onChange={(e) => setDueFrom(e.target.value)}
              style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>To</Typography>
            <input
              type="date"
              value={dueTo}
              onChange={(e) => setDueTo(e.target.value)}
              style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
            />
          </Box>
          <Button size="small" variant="contained" onClick={() => navigate("/tasks")}>
            View All
          </Button>
        </Box>
      </Box>

      {upcomingTasks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No tasks due in the selected range.
        </Typography>
      ) : (
        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
            fontSize: "0.875rem",
            color: "rgba(0, 0, 0, 0.87)",
          }}
        >
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: 8, fontWeight: 600 }}>Entity</th>
              <th style={{ textAlign: "left", padding: 8, fontWeight: 600 }}>Project</th>
              <th style={{ textAlign: "left", padding: 8, fontWeight: 600 }}>Task</th>
              <th style={{ textAlign: "left", padding: 8, fontWeight: 600 }}>Assignee</th>
              <th style={{ textAlign: "left", padding: 8, fontWeight: 600 }}>Due Date</th>
              <th style={{ textAlign: "left", padding: 8, fontWeight: 600 }}>Time Left</th>
              <th style={{ textAlign: "left", padding: 8, fontWeight: 600 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {upcomingTasks.map((t, i) => {
              const left = daysLeft(t.due);
              const badge =
                left < 0
                  ? { text: "Overdue", bg: "#ffebee", color: "#d32f2f" }
                  : left === 0
                  ? { text: "Today", bg: "#fff8e1", color: "#f57c00" }
                  : { text: `${left} day${left === 1 ? "" : "s"}`, bg: "#e3f2fd", color: "#1976d2" };

              return (
                <tr key={i} style={{ borderTop: "1px solid #eee" }}>
                  <td style={{ padding: "10px 8px" }}>{t.entity}</td>
                  <td style={{ padding: "10px 8px" }}>{t.project}</td>
                  <td style={{ padding: "10px 8px" }}>{t.task}</td>
                  <td style={{ padding: "10px 8px" }}>{t.assignee || "—"}</td>
                  <td style={{ padding: "10px 8px" }}>{fmtDate(t.due)}</td>
                  <td style={{ padding: "10px 8px" }}>
                    <span
                      style={{
                        background: badge.bg,
                        color: badge.color,
                        padding: "2px 8px",
                        borderRadius: 12,
                        fontSize: 12,
                      }}
                    >
                      {badge.text}
                    </span>
                  </td>
                  <td style={{ padding: "10px 8px" }}>{t.status || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      )}
    </Paper>
  </Grid>
</Grid>

    </Box>
  );
  }
